/**
 * Get a promise that resolves after the given time expressed in ms
 *
 * @param {number} ms the time to be waited before resolving
 * @returns {Promise} resolve after the time interval
 */
function waitMs(ms) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Get a promise that resolves after the given time expressed in seconds
 *
 * @param {number} seconds the time to be waited before resolving
 * @returns {Promise} resolve after the time interval
 */
function wait(seconds) {
	return waitMs(seconds * 1000);
}

/**
 * Get a promise that resolves at the next animation frame.
 *
 * @returns {Promise} resolve after a call to requestAnimationFrame executes the given callback
 */
function waitFrame() {
	return new Promise((resolve) => requestAnimationFrame(resolve));
}

/**
 * Get a promise that resolve after a given condition turns to a truthy value. It is not an
 * efficient way, since it uses a standard polling technique, so be careful and use it only if no
 * other better solution is available.
 *
 * @param {function} condition function that when called evaluate to a truthy or falsy value
 * @param {boolean|number} [interval=true] when true, the polling is performed at
 * *requestAnimationFrame* intervals, otherwise the number is used as ms for standard setTimeout
 * calls.
 * @returns {Promise} resolves when the condition is finally true
 */
function waitCondition(condition, interval = true) {
	return new Promise((resolve) => {
		const loop = () => {
			if (condition()) {
				resolve();
			} else {
				if (interval === true) {
					requestAnimationFrame(loop);
				} else {
					setTimeout(loop, +interval);
				}
			}
		};
		loop();
	});
}

/**
 * Get a promise that resolves after a given potentially asynchronous callback is executed on the
 * next requestAnimationFrame
 *
 * @param {function} callback function to be executed, potentially async
 * @returns {Promise} resolved after the callback execution
 */
function onFrame(callback) {
	return new Promise((resolve) => {
		requestAnimationFrame(async () => {
			await callback();
			resolve();
		});
	});
}

export {wait, wait as waitSec, waitMs, waitFrame, waitCondition, onFrame};
